import type { NextPage } from "next"

import { DEFAULT_LANGUAGE, useLocale, useTranslation } from "@bounce/i18n"
import { useReferrals } from "@bounce/referrals"
import { notFalsy } from "@bounce/util"
import type { NonNullListQueryNode } from "@bounce/util"

import { FAQs } from "./components/FAQs"
import { Hero } from "./components/Hero"
import { StoreContact } from "./components/StoreContact"
import { StoreInformation } from "./components/StoreInformation"
import { StructuredData } from "./components/StructuredData"
import { useBookNowUrl } from "./hooks/useBookNowUrl"
import { useInterpolations } from "./hooks/useInterpolations"
import { useSEO } from "./hooks/useSEO"
import StoreSliderSection from "../../../components/LuggageStorage/StoreSlider/StoreSliderSection"
import { getCityCurrency } from "../../../utils/city"
import { ReviewsSlider } from "@/components/Generic/Reviews/ReviewsSlider/ReviewsSlider"
import { HowItWorksCompact } from "@/components/LuggageStorage/HowItWorks/HowItWorksCompact/HowItWorksCompact"
import { Layout } from "@/components/LuggageStorage/Layout/Layout"
import { Marquee } from "@/components/LuggageStorage/Marquee/Marquee"
import { StorePicturesSlider } from "@/components/LuggageStorage/StorePicturesSlider/StorePicturesSlider"
import { StickyCTAProvider } from "@/contexts/StickyCTAContext"
import { useStoreContact } from "@/hooks/useStoreContact"
import type { AggregateRating } from "@/libs/bounce/aggregateRating"
import type { GetNearbyPoisQuery, Maybe } from "@/libs/bounce/bounceSdk.types.gen"
import type { CityStorageSpot, StoreData } from "@/libs/bounce/stores/types"
import type { Simplify, StoreDocumentData } from "@/libs/prismic/types"
import { getCityUrlPath, getStoreUrlPath, useStoreUrl } from "@/libs/router"
import useStepsCompact from "@/screens/Generic/StoreScreen/hooks/useStepsCompact"
import { useWeeklyReview } from "@/screens/Generic/StoreScreen/hooks/useWeeklyReview"
import type { Review } from "@/utils/reviews"

type Poi = NonNullListQueryNode<GetNearbyPoisQuery["pois"]> & {
  translatedName: string
}

export type StoreScreenProps = {
  aggregateRating: AggregateRating
  nearbyStores: CityStorageSpot[]
  pois: Poi[]
  prismicContent: Maybe<Simplify<StoreDocumentData>>
  reviews: Review[]
  store: StoreData
}

const StoreScreen: NextPage<StoreScreenProps> = ({
  aggregateRating,
  nearbyStores,
  pois,
  prismicContent,
  reviews,
  store,
}) => {
  const { t } = useTranslation("marketing-ls-store")
  const { language } = useLocale()
  const { coupon } = useReferrals.useCodes()
  const storeContact = useStoreContact({ mainStore: store, place: store.city.translatedName })
  const { path: bookNowPath, url: bookNowUrl } = useBookNowUrl({ store })
  const { absolutePath, getUrl: getStoreUrl } = useStoreUrl({ store })
  const interpolations = useInterpolations({ store, pois })
  const { metaTitle, metaDescription } = useSEO({ interpolations })
  const weeklyReview = useWeeklyReview(reviews)
  const storeSteps = useStepsCompact({ interpolations })

  const [nearbyPoi] = pois
  const alternates = store.alternateLanguages
    .map(language => ({
      language,
      href: getStoreUrl({ language }),
    }))
    .concat([{ language: "x-default", href: getStoreUrl({ language: DEFAULT_LANGUAGE }) }])
  const reviewSliderSubtitle =
    weeklyReview.length >= 10
      ? t(
          "screen.storeScreen.reviewsSlider.storePageSubtitle",
          "More than {{reviewLength}} people have stored their bags at this location in the last week",
          { reviewLength: Math.floor(weeklyReview.length / 5) * 5 },
        )
      : undefined

  return (
    <StickyCTAProvider
      config={{
        label: t("screen.storeScreen.stickyCTA.bookNow", "Book now"),
        path: bookNowPath,
        from: "Store",
        coupon,
      }}>
      <Layout
        alternates={alternates}
        languages={store.alternateLanguages}
        breadcrumbs={[
          { id: "home", name: "Home", href: "/" },
          {
            id: "stores",
            name: store.city.name,
            href: getCityUrlPath({ language, city: store.city }),
          },
          {
            id: "store",
            name: store.gmbLocationName,
            href: getStoreUrlPath({ store }),
          },
        ]}
        footerProps={{ storeContact }}
        headProps={{
          title: metaTitle,
          description: metaDescription,
          url: absolutePath,
        }}
        hideNewsletterBanner
        hideDynamicAppBanner>
        <Hero
          title={t("screen.storeScreen.heroOptimized.title", "Luggage storage - {{STORE_NAME}}", interpolations)}
          store={store}
          bookNowUrl={bookNowUrl}
          interpolations={interpolations}
        />
        <Marquee aggregateRating={aggregateRating} currency={getCityCurrency(store.city)} />
        <StoreInformation
          title={t("screen.storeScreen.storeInformationOptimized.title", "Luggage storage hours and reviews")}
          store={store}
          about={prismicContent?.about}
          pois={pois}
          interpolations={interpolations}
          className="pt-8 sm:pt-16"
          isVariant
        />
        <StorePicturesSlider
          title={t("screen.storeScreen.storeSliderOptimized.titleV2", "Book this Bounce luggage storage spot")}
          subtitle={t(
            "screen.storeScreen.storeSliderOptimized.subtitle",
            "Keep your bags safe at {{STORE_NAME}}",
            interpolations,
          )}
          className="pt-section"
          pictures={store.pictures.filter(notFalsy).map(({ externalId }) => ({
            externalId,
            alt: t(
              "screen.storeScreen.storeSlider.alt",
              "Storefront of Bounce Luggage Storage - {STORE_NAME}",
              interpolations,
            ),
          }))}
          href={bookNowUrl}
        />
        <ReviewsSlider
          className="pt-section"
          country={store.city.country}
          description={reviewSliderSubtitle}
          name="StoreReviews"
          reviews={reviews}
          title={t(
            "screen.storeScreen.reviewSliderOptimized.storePageTitle",
            "Trusted bag storage in {{CITY_NAME}}",
            interpolations,
          )}
        />
        <HowItWorksCompact
          steps={storeSteps}
          title={t(
            "screen.storeScreen.howItWorks.titleV3",
            "How luggage storage near {{FEATURED_POI_NAME}} works",
            interpolations,
          )}
          className="pt-section"
        />
        <StoreSliderSection
          name="StoreSlider"
          stores={nearbyStores}
          from="Store"
          lowestPrice={interpolations.LOWEST_PRICE}
          path={absolutePath}
          searchQuery={nearbyPoi?.query ?? ""}
          title={t(
            "screen.storeScreen.nearStoreSlider.titleV2",
            "Cheap luggage storage near {{FEATURED_POI_NAME}}",
            interpolations,
          )}
          subtitle={t(
            "screen.storeScreen.nearStoreSliderOptimized.subtitle",
            "Find convenient and affordable bag storage in {{CITY_NAME}}",
            interpolations,
          )}
          className="pt-section"
          sectionViewedMeta={{ section: "FeaturedPoiNearestStores", poiName: nearbyPoi?.name }}
        />
        <FAQs
          className="pt-section"
          place={{ ...store.city, translatedName: store.city.translatedName }}
          subtitle={t("screen.storeScreen.faqOptimized.subtitle", "about Bounce luggage storage")}
        />
        {storeContact && (
          <StoreContact
            title={t("screen.storeScreen.storeContactOptimized.title", "Bounce luggage storage address")}
            className="py-section"
            storeContact={storeContact}
          />
        )}
        <StructuredData
          store={store}
          aggregateRating={aggregateRating}
          metaDescription={metaDescription}
          reviews={reviews}
        />
      </Layout>
    </StickyCTAProvider>
  )
}

export default StoreScreen
